import { get, set, map, head, omit, reject } from 'lodash/fp';
import createReducer from '../../../../helpers/createReducer';
import {
  createFetchManyHandler,
  createSaveHandler,
  createUpdateHandler
} from '../../../../helpers/reducerRoutineCrudHandler';
import {
  fetchPosOrderByVisitTrackId,
  createPosOrder,
  completePosOrder,
  cancelPosOrder,
  adjustPosOrder,
  addPosOrderItem,
  updatePosOrderItemQuantity,
  deletePosOrderItem
} from '../../sagas/routines';
import {
  setFetchPosOrderError,
  clearPosOderSavingError,
  removeDeletedPosOrderItem
} from '../../actions';
import createOrderItemKey from '../../../../helpers/pos/createOrderItemKey';

const initialState = {
  all: {},
  fetching: false,
  fetched: false,
  fetchingError: null,
  saving: false,
  saved: false,
  savingError: null,
  savingItem: false,
  savedItem: false,
  savingItemErrors: null,
  deletingItem: false,
  deletedItem: false,
  deletingItemError: null
};

const setFetchPosOrderErrorHandler = action => ({
  [action.toString()]: (state, { payload: error }) => ({
    ...state,
    fetchingError: error
  })
});

const clearPosOrderSavingErrorHandler = action => ({
  [action.toString()]: set('savingError', null)
});

const addPosOrderItemHandler = routine => ({
  [routine.TRIGGER]: state => ({
    ...state,
    savingItem: true,
    savedItem: false
  }),
  [routine.SUCCESS]: (state, { payload: { response: orderItem } }) => {
    const { posOrderId } = orderItem;
    const { items = [], ...order } = get(`all.${posOrderId}`, state);
    const updatedOrder = set('items', [...items, orderItem], order);
    return set(`all.${posOrderId}`, updatedOrder, state);
  },
  [routine.FAILURE]: (state, { payload: error }) =>
    set('savingItemErrors', error, state),
  [routine.FULFILL]: state => ({
    ...state,
    savingItem: false,
    savedItem: true
  })
});

const updatePosOrderItemQuantityHandler = routine => ({
  [routine.TRIGGER]: (state, { payload: { orderItem } }) => ({
    ...state,
    savingItem: true,
    savedItem: false,
    savingItemErrors: omit(
      createOrderItemKey(orderItem),
      state.savingItemErrors
    )
  }),
  [routine.SUCCESS]: (state, { payload: { response: orderItem } }) => {
    const { posOrderId } = orderItem;
    const { items, ...order } = get(`all.${posOrderId}`, state);
    const updatedItems = map(
      item => (item.id === orderItem.id ? orderItem : item),
      items
    );
    return set(`all.${posOrderId}`, { ...order, items: updatedItems }, state);
  },
  [routine.FAILURE]: (state, { payload: { error } }) => {
    const { message, field } = head(error);
    return set(`savingItemErrors.${field}`, message, state);
  },
  [routine.FULFILL]: state => ({
    ...state,
    savingItem: false,
    savedItem: true
  })
});

const deletePosOrderItemHandler = routine => ({
  [routine.TRIGGER]: state => ({
    ...state,
    deletingItem: true,
    deletedItem: false
  }),
  [routine.FAILURE]: (state, { payload: error }) =>
    set('deletingItemError', error, state),
  [routine.FULFILL]: state => ({
    ...state,
    deletingItem: false,
    deletedItem: true
  })
});

const removeDeletedPosOrderItemHandler = action => ({
  [action.toString()]: (state, { payload: deletedItem }) => {
    const { id, posOrderId } = deletedItem;
    const { items, ...order } = get(`all.${posOrderId}`, state);
    const updatedItems = reject({ id }, items);
    return set(`all.${posOrderId}`, { ...order, items: updatedItems }, state);
  }
});

export default createReducer(initialState, {
  ...createFetchManyHandler(fetchPosOrderByVisitTrackId),
  ...setFetchPosOrderErrorHandler(setFetchPosOrderError),
  ...clearPosOrderSavingErrorHandler(clearPosOderSavingError),
  ...createSaveHandler(createPosOrder),
  ...createUpdateHandler(completePosOrder),
  ...createUpdateHandler(cancelPosOrder),
  ...createUpdateHandler(adjustPosOrder),
  ...addPosOrderItemHandler(addPosOrderItem),
  ...updatePosOrderItemQuantityHandler(updatePosOrderItemQuantity),
  ...deletePosOrderItemHandler(deletePosOrderItem),
  ...removeDeletedPosOrderItemHandler(removeDeletedPosOrderItem)
});
